import React from 'react';
import { Select, Paper, Group as MantineGroup, Text, Loader, Box, Badge } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGroup } from '../../contexts/GroupContext';
import { useAuthStore } from '../../stores/authStore';
import { IconUsersGroup } from '@tabler/icons-react';

export function GroupSelector() {
  const { availableGroups, selectedGroup, setSelectedGroup, isLoading, error } = useGroup();
  const { hasRole, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = hasRole('admin');
  
  // Show loading state
  if (isLoading) {
    return (
      <Paper shadow="sm" p="md" mb="xl" withBorder>
        <MantineGroup position="center">
          <Loader size="sm" />
          <Text>Loading groups...</Text>
        </MantineGroup>
      </Paper>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Paper shadow="sm" p="md" mb="xl" withBorder>
        <Text color="red">{error}</Text>
      </Paper>
    );
  }
  
  // Only display the component for:
  // 1. Admin users (regardless of how many groups they have)
  // 2. Regular users with multiple groups (based on their actual group membership)
  const userGroupCount = user?.allGroups?.length || 0;
  if (!isAdmin && userGroupCount <= 1) {
    return null;
  }
  
  const handleGroupChange = (value: string | null) => {
    if (value) {
      setSelectedGroup(value);
      
      // Navigate to dashboard if not already there
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    }
  };

  // Transform groups into select options format
  const groupOptions = availableGroups.map(group => ({
    value: group.id,
    label: group.displayName || formatGroupId(group.id)
  }));

  return (
    <Paper shadow="sm" p="md" mb="xl" withBorder>
      <MantineGroup position="apart">
        <Text weight={500}>
          {isAdmin ? 'View Data for Group:' : 'Select Your Group:'}
        </Text>
        <Select
          data={groupOptions}
          value={selectedGroup || undefined}
          onChange={handleGroupChange}
          placeholder="Select a group"
          style={{ minWidth: 260, flex: 1, maxWidth: '100%' }}
          styles={{
            input: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            dropdown: {
              width: 'max-content',
              maxWidth: 'calc(100vw - 32px)',
            },
            item: {
              whiteSpace: 'normal',
            },
          }}
        />
      </MantineGroup>
      {isAdmin && (
        <Text size="xs" color="dimmed" mt="xs">
          As an admin, you can view data for all groups.
        </Text>
      )}
    </Paper>
  );
}

// Compact version of the GroupSelector for use in the navbar
export function NavbarGroupSelector() {
  const { availableGroups, selectedGroup, setSelectedGroup, isLoading, error } = useGroup();
  const { hasRole, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = hasRole('admin');
  
  // Don't show for regular users with only one group (based on their actual group membership)
  const userGroupCount = user?.allGroups?.length || 0;
  if (!isAdmin && userGroupCount <= 1) {
    return null;
  }
  
  // For loading/error states, show minimal indicators in navbar
  if (isLoading) {
    return (
      <Box mx="md">
        <Loader size="xs" />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Badge color="red" variant="filled" size="sm">
        Error
      </Badge>
    );
  }
  
  const handleGroupChange = (value: string | null) => {
    if (value) {
      setSelectedGroup(value);
      
      // Navigate to dashboard if not already there
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    }
  };

  // Transform groups into select options format
  const groupOptions = availableGroups.map(group => ({
    value: group.id,
    label: group.displayName || formatGroupId(group.id)
  }));

  return (
    <MantineGroup spacing="xs" noWrap>
      <IconUsersGroup size={16} />
      <Select
        data={groupOptions}
        value={selectedGroup || undefined}
        onChange={handleGroupChange}
        placeholder="Select group"
        size="xs"
        variant="filled"
        radius="md"
        sx={(theme) => ({
          width: 'min(260px, 60vw)',
          '.mantine-Select-input': {
            fontWeight: 500,
          }
        })}
        styles={(theme) => ({
          input: {
            backgroundColor: theme.colorScheme === 'dark' 
              ? theme.colors.dark[5] 
              : theme.colors.gray[1],
            border: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }
          ,
          dropdown: {
            width: 'max-content',
            maxWidth: 'calc(100vw - 32px)',
          },
          item: {
            whiteSpace: 'normal',
          },
        })}
      />
    </MantineGroup>
  );
}

// Helper function to format group IDs if displayName is not available
function formatGroupId(groupId: string): string {
  // Format group name - remove technical prefixes, format names
  // This is a simple example - adapt as needed for your group naming convention
  
  // If group ID is a UUID, you might have a mapping of friendly names
  if (groupId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    // Return a shortened version of the UUID
    return `Group ${groupId.substring(0, 8)}...`;
  }
  
  // Format more readable group names
  return groupId
    .replace(/^group_/, '') // Remove common prefixes
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
} 